import React, {useEffect, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {
  faArrowUpRightFromSquare,
  faBed,
  faCamera,
  faCheckCircle,
  faHammer,
  faGraduationCap,
  faHotel,
  faLocationDot,
  faMessage,
  faPencilAlt,
  faPlane,
  faPlus,
  faStar,
  faTimes,
  faTrain,
  faUmbrellaBeach
} from "@fortawesome/free-solid-svg-icons";
import {addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, getCountFromServer, getDocs, onSnapshot, query, updateDoc, where} from "@firebase/firestore";
import {db} from "../../../firebase/FirebaseProvider";
import {Location, User} from "../../types/Types";
import {LocationTag, LocationTagOption, ManagedTag} from "../../types/LocationTags";
import {getCountryFlag, getGoogleMapsLink} from "../../util/Util";
import LikeButton from "../../tag/likes/LikeButton";
import LocationCheckIn from "./LocationCheckIn";
import LocationStayButton from "./LocationStayButton";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import "./LocationDetailsV2.css";

interface Props {
  location: Location | undefined;
  currentUser: User | undefined;
  likesMap: Map<string, boolean>;
  onLikeCountChange?: (commentId: string, adjustment: number) => void;
  visited?: boolean;
  onToggleVisited?: () => void;
  variant?: "header" | "location" | "tags" | "widgets";
}

function formatAddress(address: string | undefined): string {
  if (!address) {
    return "";
  }

  const seenParts = new Set<string>();
  return address
    .split(",")
    .map(part => part.trim())
    .filter(part => {
      const normalisedPart = part.toLowerCase();
      if (!part || seenParts.has(normalisedPart)) {
        return false;
      }

      seenParts.add(normalisedPart);
      return true;
    })
    .join(", ");
}

function getLocationTypeLabels(location: Location): Array<{label: string, icon: IconDefinition, iconClassName: string}> {
  return [
    location.hotel ? {label: "Hotel", icon: faHotel, iconClassName: "icon-hotel"} : undefined,
    location.airport ? {label: "Airport", icon: faPlane, iconClassName: "icon-plane"} : undefined,
    location.holidayPark ? {label: "Holiday park", icon: faUmbrellaBeach, iconClassName: "icon-holidaypark"} : undefined,
    location.isUniversity ? {label: "University", icon: faGraduationCap, iconClassName: "icon-university"} : undefined,
    location.trainStation ? {label: "Train station", icon: faTrain, iconClassName: "icon-train"} : undefined,
    location.bonus ? {label: "Bonus pub", icon: faStar, iconClassName: "icon-bonus"} : undefined,
  ].filter((locationType): locationType is {label: string, icon: IconDefinition, iconClassName: string} => !!locationType);
}

function getValidDate(dateString: string | undefined): Date | undefined {
  if (!dateString) {
    return undefined;
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date;
}

function getPubAge(location: Location): string {
  const openingDate = getValidDate(location.openingDate);
  if (!openingDate) {
    return "Unknown";
  }

  const closingDate = getValidDate(location.closingDate);
  if (location.pubIsClosed && !closingDate) {
    return "Unknown";
  }

  const endDate = closingDate || new Date();
  if (endDate < openingDate) {
    return "1d";
  }

  const dayCount = Math.max(
    Math.floor((endDate.getTime() - openingDate.getTime()) / (1000 * 60 * 60 * 24)),
    1
  );

  let monthCount = (endDate.getFullYear() - openingDate.getFullYear()) * 12;
  monthCount += endDate.getMonth() - openingDate.getMonth();

  if (endDate.getDate() < openingDate.getDate()) {
    monthCount--;
  }

  if (monthCount < 1) {
    return `${dayCount}d`;
  }

  const years = Math.floor(monthCount / 12);
  const months = monthCount % 12;

  if (years > 0 && months > 0) {
    return `${years}y ${months}m`;
  }

  if (years > 0) {
    return `${years}y`;
  }

  return `${months}m`;
}

function LocationDetailsV2(props: Props) {
  const [showTagModal, setShowTagModal] = useState<boolean>(false);
  const [showInlineMap, setShowInlineMap] = useState<boolean>(false);
  const [savingTagId, setSavingTagId] = useState<string | undefined>(undefined);
  const [photoCount, setPhotoCount] = useState<number>(0);
  const [commentCount, setCommentCount] = useState<number>(0);
  const [locationStayCount, setLocationStayCount] = useState<number>(() => Number(props.location?.stayCount || 0));
  const [locationTags, setLocationTags] = useState<Array<LocationTag>>([]);
  const [locationTagValues, setLocationTagValues] = useState<Array<string>>([]);
  const [locationTagOptions, setLocationTagOptions] = useState<Array<LocationTagOption>>([]);
  const [isLoadingLocationTagOptions, setIsLoadingLocationTagOptions] = useState<boolean>(true);
  const variant = props.variant || "header";

  const getTagCreatedAtTime = (createdAt: any): number => {
    if (!createdAt) {
      return Number.MAX_SAFE_INTEGER;
    }

    if (typeof createdAt.toDate === "function") {
      return createdAt.toDate().getTime();
    }

    return new Date(createdAt).getTime();
  };

  const getLocationTagOption = (tagValue: string): LocationTagOption | undefined => {
    return locationTagOptions.find((tagOption) => tagOption.id === tagValue);
  };

  const tagStatsById = locationTags.reduce((tagsMap, tag) => {
    const existingTag = tagsMap.get(tag.tagId);
    if (existingTag) {
      existingTag.userIds.add(tag.userId);
      existingTag.createdAtTime = Math.min(existingTag.createdAtTime, getTagCreatedAtTime(tag.createdAt));
    } else {
      tagsMap.set(tag.tagId, {
        tagId: tag.tagId,
        tagName: tag.tagName,
        userIds: new Set<string>([tag.userId]),
        createdAtTime: getTagCreatedAtTime(tag.createdAt),
      });
    }

    return tagsMap;
  }, new Map<string, {tagId: string, tagName: string, userIds: Set<string>, createdAtTime: number}>());

  const displayedLocationTags = Array.from(new Set(locationTagValues)).flatMap((tagValue, index) => {
    const tagOption = getLocationTagOption(tagValue);
    if (!tagOption) {
      return [];
    }

    const tagId = tagOption.id || tagValue;
    const tagStats = tagStatsById.get(tagId);
    return {
      tagId,
      tagName: tagOption.name,
      userIds: tagStats?.userIds || new Set<string>(),
      createdAtTime: tagStats?.createdAtTime || Number.MAX_SAFE_INTEGER + index,
    };
  }).sort((tagA, tagB) => {
    const countDifference = tagB.userIds.size - tagA.userIds.size;
    if (countDifference !== 0) {
      return countDifference;
    }

    return tagA.createdAtTime - tagB.createdAtTime;
  });

  useEffect(() => {
    setLocationTagValues(props.location?.tags || []);
  }, [props.location]);

  useEffect(() => {
    setLocationStayCount(Number(props.location?.stayCount || 0));
  }, [props.location?.id]);

  useEffect(() => {
    const locationId = props.location?.id;
    if ((props.variant !== "location" && props.variant !== "widgets") || !locationId) {
      setLocationStayCount(0);
      return;
    }

    return onSnapshot(doc(db, "locations", locationId), (locationSnapshot) => {
      setLocationStayCount(Number(locationSnapshot.data()?.stayCount || 0));
    });
  }, [props.location?.id, props.variant]);

  useEffect(() => {
    const locationId = props.location?.id;
    if (!locationId) {
      return;
    }

    const handleStayCountChanged = (event: Event) => {
      const detail = (event as CustomEvent<{locationId?: string; adjustment?: number; locationStayCount?: number}>).detail;
      if (detail?.locationId !== locationId) {
        return;
      }

      if (typeof detail.locationStayCount === "number") {
        setLocationStayCount(detail.locationStayCount);
      } else if (typeof detail.adjustment === "number") {
        setLocationStayCount((count) => Math.max(0, count + detail.adjustment!));
      }
    };

    window.addEventListener("stl:stay-count-changed", handleStayCountChanged);
    return () => window.removeEventListener("stl:stay-count-changed", handleStayCountChanged);
  }, [props.location?.id]);

  useEffect(() => {
    (async () => {
      await loadLocationTags();
    })();
  }, [props.location]);

  useEffect(() => {
    (async () => {
      await loadLocationTagOptions();
    })();
  }, []);

  useEffect(() => {
    if (props.location?.pubIsClosed) {
      setShowTagModal(false);
    }
  }, [props.location]);

  useEffect(() => {
    if (!props.location || variant !== "widgets") {
      return;
    }

    (async () => {
      await loadLocationContentCounts();
    })();
  }, [props.location, variant]);

  const loadLocationTags = async() => {
    if (!props.location) {
      setLocationTags([]);
      return;
    }

    const locationTagsQuery = query(
      collection(db, 'location-tags'),
      where('locationId', '==', props.location.id)
    );
    const locationTagsSnapshot = await getDocs(locationTagsQuery);
    const tags: Array<LocationTag> = [];
    locationTagsSnapshot.forEach((tagDoc) => {
      const tag = tagDoc.data() as LocationTag;
      tags.push({
        ...tag,
        id: tagDoc.id,
      });
    });
    setLocationTags(tags);
  };

  const loadLocationTagOptions = async() => {
    setIsLoadingLocationTagOptions(true);
    try {
      const locationTagOptionsQuery = query(
        collection(db, 'tags'),
        where('type', '==', 'LOCATION')
      );
      const locationTagOptionsSnapshot = await getDocs(locationTagOptionsQuery);
      const loadedTagOptions: Array<LocationTagOption> = [];

      locationTagOptionsSnapshot.forEach((tagDoc) => {
        const tag = tagDoc.data() as ManagedTag;
        if (tag.status === 'ACTIVE') {
          loadedTagOptions.push({
            id: tagDoc.id,
            name: tag.name,
            description: tag.description,
          });
        }
      });

      setLocationTagOptions(
        loadedTagOptions.sort((tagA, tagB) => tagA.name.localeCompare(tagB.name))
      );
    } catch (error) {
      console.error("Error loading location tag options: ", error);
    } finally {
      setIsLoadingLocationTagOptions(false);
    }
  };

  const loadLocationContentCounts = async() => {
    if (!props.location) {
      setPhotoCount(0);
      setCommentCount(0);
      return;
    }

    try {
      const photosQuery = query(
        collection(db, 'photos'),
        where('locationId', '==', props.location.id),
        where('hidden', '==', false)
      );
      const commentsQuery = query(
        collection(db, 'comments'),
        where('locationId', '==', props.location.id),
        where('hidden', '==', false)
      );
      const [photosSnapshot, commentsSnapshot] = await Promise.all([
        getCountFromServer(photosQuery),
        getCountFromServer(commentsQuery),
      ]);

      setPhotoCount(photosSnapshot.data().count);
      setCommentCount(commentsSnapshot.data().count);
    } catch (error) {
      console.error("Error loading location content counts: ", error);
    }
  };

  const toggleLocationTag = async(tag: LocationTagOption) => {
    if (!props.location || props.location.pubIsClosed || !props.currentUser || savingTagId) {
      return;
    }

    setSavingTagId(tag.id);
    try {
      const currentUserTags = locationTags.filter((locationTag) => (
        locationTag.tagId === tag.id && locationTag.userId === props.currentUser!.id
      ));

      if (currentUserTags.length > 0) {
        await Promise.all(
          currentUserTags
            .filter((locationTag) => !!locationTag.id)
            .map((locationTag) => deleteDoc(doc(db, 'location-tags', locationTag.id!)))
        );

        const remainingTags = locationTags.filter((locationTag) => (
          !(locationTag.tagId === tag.id && locationTag.userId === props.currentUser!.id)
        ));
        const hasRemainingTag = remainingTags.some((locationTag) => (
          locationTag.tagId === tag.id
        ));

        if (!hasRemainingTag) {
          await updateDoc(doc(db, 'locations', props.location.id), {
            tags: arrayRemove(tag.id),
            lastModified: new Date(),
          });
          setLocationTagValues((existingTagValues) => (
            existingTagValues.filter((tagValue) => tagValue !== tag.id)
          ));
        }

        setLocationTags(remainingTags);
        return;
      }

      const locationTag: LocationTag = {
        tagId: tag.id,
        tagName: tag.name,
        userId: props.currentUser.id,
        userName: props.currentUser.name,
        locationId: props.location.id,
        createdAt: new Date(),
      };
      const locationTagRef = await addDoc(collection(db, 'location-tags'), locationTag);
      await updateDoc(doc(db, 'locations', props.location.id), {
        tags: arrayUnion(tag.id),
        lastModified: new Date(),
      });
      setLocationTagValues((existingTagValues) => (
        existingTagValues.includes(tag.id) ? existingTagValues : [...existingTagValues, tag.id]
      ));
      setLocationTags((existingTags) => [
        ...existingTags,
        {
          ...locationTag,
          id: locationTagRef.id,
        },
      ]);
      setShowTagModal(false);
    } catch (error) {
      console.error("Error adding location tag: ", error);
    } finally {
      setSavingTagId(undefined);
    }
  };

  if (!props.currentUser) {
    return null;
  }

  const mapQuery = props.location ? `${props.location.lat},${props.location.lng}` : "";
  const mapEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=16&output=embed`;

  const locationTypeLabels = props.location ? getLocationTypeLabels(props.location) : [];
  const currentStayCount = props.location ? locationStayCount : 0;
  const pubStats = props.location
    ? [
        {
          label: props.location.pubIsClosed ? "Open For" : "Age",
          value: getPubAge(props.location),
        },
      ].filter((stat) => stat.value.toLowerCase() !== "unknown")
    : [];
  const toggleInlineMap = () => setShowInlineMap((showMap) => !showMap);
  const handleInlineMapKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleInlineMap();
    }
  };
  const locationWidgets = props.location ? [
    {
      label: "Visits",
      icon: faCheckCircle,
      to: `/location/${props.location.id}/visits`,
      value: props.location.visitCount || 0,
    },
    {
      label: "Check-ins",
      icon: faLocationDot,
      to: `/location/${props.location.id}/sessions`,
      value: props.location.sessionUniqueCount || 0,
    },
    {
      label: "Photos",
      icon: faCamera,
      to: `/location/${props.location.id}/photos`,
      value: photoCount,
    },
    {
      label: "Comments",
      icon: faMessage,
      to: `/location/${props.location.id}/comments?view=comments`,
      value: commentCount,
    },
    ...(props.location.hotel ? [{
      label: "Stays",
      icon: faBed,
      to: `/location/${props.location.id}/stays`,
      value: currentStayCount,
    }] : []),
  ] : [];
  const visibleLocationWidgets = locationWidgets.filter(widget => widget.value > 0);

  return (
    <>
      {variant === "header" && <V2GlassPanel className="location-v2-header">
        {!props.location && <div className="location-v2-loading">...</div>}
        {props.location && (
          <>
            <V2GlassSurface className="location-v2-title-glass">
              <div className="location-v2-kicker">
                <span className="location-v2-country-flag">{getCountryFlag(props.location.country || '')}</span>
                <span>{props.location.city || props.location.country}</span>
                {props.location.openingDate && (
                  <span>EST. {new Date(props.location.openingDate).getFullYear()}{props.location.pubIsClosed && ` - ${props.location.closingDate ? new Date(props.location.closingDate).getFullYear() : "?"}`}</span>
                )}
              </div>
              <div className="location-v2-name-row">
                <h1>{props.location.name}</h1>
                {props.location.pubIsClosed && (
                  <V2GlassChip className="location-v2-type-chip location-v2-status-chip">
                    <FontAwesomeIcon className="icon-closed" icon={faTimes} />
                    Closed
                  </V2GlassChip>
                )}
                {props.location.pubIsTemporaryClosed && !props.location.pubIsClosed && (
                  <V2GlassChip className="location-v2-type-chip location-v2-status-chip">
                    <FontAwesomeIcon className="icon-temporary" icon={faHammer} />
                    Renovating
                  </V2GlassChip>
                )}
                {locationTypeLabels.map((locationType) => (
                  <V2GlassChip className="location-v2-type-chip" key={locationType.label}>
                    <FontAwesomeIcon className={locationType.iconClassName} icon={locationType.icon} />
                    {locationType.label}
                  </V2GlassChip>
                ))}
              </div>
              <div className="location-v2-header-controls">
                <LikeButton
                  entityId={props.location.id}
                  likeType="LOCATION"
                  initialLikeCount={props.location.likeCount || 0}
                  isLiked={props.likesMap.get(props.location.id) || false}
                  currentUser={props.currentUser}
                  locationId={props.location.id}
                  locationName={props.location.name}
                  onLikeCountChange={props.onLikeCountChange}
                  unlikedColour="rgba(48, 54, 61, 0.45)"
                  buttonClassName="location-v2-heart-button"
                />
                {props.onToggleVisited && (
                  <button className={"location-v2-visited-button " + (props.visited ? "location-v2-visited-button-active" : "")} type="button" onClick={props.onToggleVisited}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </button>
                )}
                {props.location.hotel && (
                  <LocationStayButton location={props.location} currentUser={props.currentUser} variant="v2" />
                )}
                {props.currentUser.admin && (
                  <Link className="location-v2-edit-button" to={`/admin/${props.location.id}`} aria-label="Edit pub details">
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </Link>
                )}
                {!props.currentUser.socialDisabled && (
                  <LocationCheckIn currentUser={props.currentUser} location={props.location} variant="v2Header" />
                )}
              </div>
            </V2GlassSurface>
          </>
        )}
      </V2GlassPanel>}

      {variant === "location" && props.location && (
        <V2GlassPanel className="location-v2-location-panel">
          <V2GlassSurface
            className="location-v2-location-widget"
            onClick={toggleInlineMap}
            onKeyDown={handleInlineMapKeyDown}
            role="button"
            tabIndex={0}
            aria-expanded={showInlineMap}
            aria-label={showInlineMap ? "Hide map" : "Show map"}
          >
            <div className="location-v2-address-button">
              <span>{formatAddress(props.location.address)}</span>
              <span className="location-v2-address-hint">{showInlineMap ? "Hide map" : "Show map"}</span>
            </div>
            <a
              aria-label="Open in Google Maps"
              className="location-v2-map-action"
              href={getGoogleMapsLink(props.location.name + ', ' + props.location.city)}
              onClick={(event) => event.stopPropagation()}
              target="_blank"
              rel="noreferrer"
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </a>
          </V2GlassSurface>
          {showInlineMap && (
            <div className="location-v2-inline-map">
              <iframe
                title={`${props.location.name} map`}
                src={mapEmbedUrl}
                className="location-v2-map-frame"
                loading="lazy"
              />
            </div>
          )}
        </V2GlassPanel>
      )}

      {variant === "tags" && props.location && !props.location.pubIsClosed && (
        <V2GlassPanel className="location-v2-tags-panel">
          <div className="location-v2-tags">
            {displayedLocationTags.map((tag) => (
              <V2GlassChip className="location-v2-tag" key={tag.tagId}>
                {tag.tagName}
                {tag.userIds.size > 1 && <b>{tag.userIds.size}</b>}
              </V2GlassChip>
            ))}
            {!props.location.pubIsClosed && (
              <button className="location-v2-tag-button" type="button" onClick={() => setShowTagModal(true)}>
                <FontAwesomeIcon icon={faPlus} /> tag
              </button>
            )}
          </div>
        </V2GlassPanel>
      )}

      {variant === "widgets" && props.location && visibleLocationWidgets.length > 0 && (
        <>
        <V2GlassPanel className="location-v2-widgets-panel">
            <div className="location-v2-widget-grid">
              {visibleLocationWidgets.map(widget => (
                widget.to ? <Link className="location-v2-widget-link" to={widget.to} key={widget.label}>
                  <V2GlassSurface className="location-v2-widget">
                    <span><FontAwesomeIcon icon={widget.icon} /> {widget.label}</span>
                    <b>{widget.value}</b>
                  </V2GlassSurface>
                </Link> : <div className="location-v2-widget-link" key={widget.label}>
                  <V2GlassSurface className="location-v2-widget">
                    <span><FontAwesomeIcon icon={widget.icon} /> {widget.label}</span>
                    <b>{widget.value}</b>
                  </V2GlassSurface>
                </div>
              ))}
            </div>
        </V2GlassPanel>
        {pubStats.length > 0 && (
          <V2GlassPanel className="location-v2-pub-stats-panel">
            <h2>Pub Stats</h2>
            <V2GlassSurface className="location-v2-pub-stats-table">
              {pubStats.map((stat) => (
                <div className="location-v2-pub-stat-row" key={stat.label}>
                  <span>{stat.label}</span>
                  <b>{stat.value}</b>
                </div>
              ))}
            </V2GlassSurface>
          </V2GlassPanel>
        )}
        </>
      )}

      <Modal
        show={showTagModal && !props.location?.pubIsClosed}
        onHide={() => setShowTagModal(false)}
        dialogClassName="location-tag-modal"
        centered
      >
        <Modal.Header className="location-tag-modal-header">
          <Modal.Title>Add tag</Modal.Title>
          <button className="location-tag-modal-close" type="button" aria-label="Close" onClick={() => setShowTagModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="location-tag-options">
            {locationTagOptions.map((tag) => {
              const userHasTag = locationTags.some((locationTag) => (
                locationTag.tagId === tag.id && locationTag.userId === props.currentUser?.id
              ));

              return (
                <div key={tag.id} className="location-tag-option">
                  <Button
                    className="location-tag-option-button"
                    variant={userHasTag ? "secondary" : "outline-secondary"}
                    disabled={!!savingTagId}
                    onClick={() => toggleLocationTag(tag)}
                  >
                    {savingTagId === tag.id ? (userHasTag ? "Removing..." : "Saving...") : tag.name}
                  </Button>
                  <span className="location-tag-option-description">{tag.description}</span>
                </div>
              );
            })}
            {!isLoadingLocationTagOptions && locationTagOptions.length === 0 && (
              <div className="text-center text-muted">No tags available</div>
            )}
            {isLoadingLocationTagOptions && (
              <div className="text-center text-muted">Loading tags...</div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default LocationDetailsV2;
